import { useTranslation } from "react-i18next";
import { ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/shared/lib/utils";
import { TestPartitionBadge } from "@/shared/components/TestPartitionBadge";
import type { PropertyListItem } from "./types";
import { formatDateShort, formatPropertyLocation } from "./format-property";
import { PropertyStatusBadge } from "./PropertyStatusBadge";

interface PropertiesTableProps {
  readonly items: readonly PropertyListItem[];
}

export function PropertiesTable({ items }: PropertiesTableProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  return (
    <div
      data-testid="properties-table"
      className="overflow-x-auto rounded-lg border border-border bg-card"
    >
      <table className="w-full text-sm">
        <thead className="border-b border-border bg-muted/30 text-left">
          <tr>
            <Th>{t("properties.table.property")}</Th>
            <Th>{t("properties.table.status")}</Th>
            <Th>{t("properties.table.owner")}</Th>
            <Th>{t("properties.table.created")}</Th>
            <Th className="w-12">
              <span className="sr-only">{t("properties.table.openDetail")}</span>
            </Th>
          </tr>
        </thead>
        <tbody>
          {items.map((property) => (
            <tr
              key={property.id}
              data-testid={`property-row-${property.id}`}
              onClick={() => navigate(`/properties/${encodeURIComponent(property.id)}`)}
              className="cursor-pointer border-b border-border transition-colors last:border-0 hover:bg-muted/40"
            >
              <Td>
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <span className="font-sans font-medium text-foreground">{property.name}</span>
                    <TestPartitionBadge
                      isTest={property.isTest}
                      testId={`property-test-badge-${property.id}`}
                    />
                  </div>
                  <span className="font-sans text-xs text-muted-foreground">
                    {formatPropertyLocation(property) || "—"}
                  </span>
                </div>
              </Td>
              <Td>
                <PropertyStatusBadge status={property.status} />
              </Td>
              <Td>
                <div className="flex flex-col">
                  <span className="font-sans text-foreground">{property.ownerName || "—"}</span>
                  <span className="font-sans text-xs text-muted-foreground">
                    {property.ownerEmail || "—"}
                  </span>
                </div>
              </Td>
              <Td className="text-muted-foreground">
                {formatDateShort(property.createdAt, i18n.language) || "—"}
              </Td>
              <Td className="text-right">
                <ChevronRight
                  className="ml-auto h-4 w-4 text-muted-foreground"
                  aria-hidden="true"
                />
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({
  children,
  className,
}: {
  readonly children: React.ReactNode;
  readonly className?: string;
}) {
  return (
    <th
      scope="col"
      className={cn(
        "px-4 py-3 font-sans text-xs font-semibold uppercase tracking-wide text-muted-foreground",
        className,
      )}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  className,
}: {
  readonly children: React.ReactNode;
  readonly className?: string;
}) {
  return <td className={cn("px-4 py-3 align-middle", className)}>{children}</td>;
}
